// Tiny i18n hook — no library dependency. Reads selected language from
// localStorage; defaults to "en". Triggers a re-render across the app via
// a custom event when the operator switches language so nothing is stale.
import { useEffect, useState, useCallback } from "react";
import { TRANSLATIONS, SUPPORTED_LANGUAGES } from "./translations";

const STORAGE_KEY = "zcd_lang";

function readLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && TRANSLATIONS[saved]) return saved;
  } catch { /* ignore */ }
  return "en";
}

export function useTranslation() {
  const [lang, setLang] = useState(readLang);

  useEffect(() => {
    const onChange = () => setLang(readLang());
    window.addEventListener("zcd:lang-change", onChange);
    return () => window.removeEventListener("zcd:lang-change", onChange);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
      let s = dict[key] ?? TRANSLATIONS.en[key] ?? key;
      if (vars && typeof s === "string") {
        for (const [k, v] of Object.entries(vars)) {
          s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
        }
      }
      return s;
    },
    [lang],
  );

  const setLanguage = useCallback((next) => {
    if (!TRANSLATIONS[next]) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent("zcd:lang-change"));
  }, []);

  return { t, lang, setLanguage, languages: SUPPORTED_LANGUAGES };
}

// Convenience: get the current language code without subscribing to changes
export function getCurrentLanguage() {
  return readLang();
}
